import React, { useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import Loading from "react-fullscreen-loading";
import FilterListIcon from "@material-ui/icons/FilterList";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import _ from "lodash";

import Header from "@components/Header";
import { ModalVehicleAndTravelDetails } from "@components/Modal/VehicleAndTravelDetails";
import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { Travel } from "@shared/interfaces/travel.interface";
import { TravelCyclePlaceRow } from "@shared/interfaces/travel-cycle-analysis.interface";
import api from "@services/api";
import { useToast } from "@hooks/useToast";
import utils from "@utils/useful-functions";

import useTravelAnalysis from "./hooks/useTravelAnalysis";
import useTravelAnalysisAlerts from "./hooks/useTravelAnalysisAlerts";
import useTravelCycleAnalysis from "./hooks/useTravelCycleAnalysis";
import useTravelCycleDrilldown from "./hooks/useTravelCycleDrilldown";
import KpiCards from "./components/molecules/KpiCards";
import QueryFilterTravelAnalysis from "./components/organisms/QueryFilterTravelAnalysis";
import AlertDistributionChart from "./components/organisms/AlertDistributionChart";
import AlertsDrilldownModal from "./components/organisms/AlertsDrilldownModal";
import AlertGlossary from "./components/organisms/AlertGlossary";
import CycleDaily from "./components/organisms/CycleDaily";
import CycleImpact from "./components/organisms/CycleImpact";
import CyclePlaces from "./components/organisms/CyclePlaces";
import HardwareToCheck from "./components/organisms/HardwareToCheck";
import CycleDrilldownModal from "./components/organisms/CycleDrilldownModal";
import { AlertByType, TravelAnalysisFilter } from "./types";

import {
  Container,
  PageTitleSection,
  TopBar,
  TopBarActions,
  FilterButton,
  HelpButton,
  QuickFilterChip,
  SectionBlock,
  SectionTitle
} from "./styles";

/** Regional or plant clicked in the breakdown table, narrowing the cycle analysis until removed. */
interface QuickPlaceFilter {
  kind: "regional" | "plant";
  id: string;
  name: string;
}

/**
 * Travel Analysis page: production cycle of the ticket travels (complete cycle, cause of each failure,
 * regional and plant breakdown, hardware to check) and, below it, the telemetry alerts.
 */
const TravelAnalysis: React.FC = () => {

  const screen = useSelector(({ screen: state }: any) => state);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [openFilter, setOpenFilter] = useState(false);
  const [openGlossary, setOpenGlossary] = useState(false);
  const [quickFilter, setQuickFilter] = useState<QuickPlaceFilter | null>(null);
  const { metrics, loading, error, currentFilter, handleFilter } = useTravelAnalysis();
  const drilldown = useTravelAnalysisAlerts(currentFilter);
  const impactRef = useRef<HTMLDivElement | null>(null);
  const hardwareRef = useRef<HTMLDivElement | null>(null);

  /** Filter of the cycle sections: the screen filter narrowed by the regional or plant clicked in the table. */
  const cycleFilter = useMemo<TravelAnalysisFilter | null>(() => {
    if (!currentFilter || !quickFilter) return currentFilter;

    return quickFilter.kind === "regional"
      ? { ...currentFilter, idRegionals: [quickFilter.id], idPlants: [] }
      : { ...currentFilter, idPlants: [quickFilter.id] };
  }, [currentFilter, quickFilter]);
  const { summary, loading: cycleLoading } = useTravelCycleAnalysis(cycleFilter);
  const cycleDrilldown = useTravelCycleDrilldown(cycleFilter);
  const periodLabel = cycleFilter?.from && cycleFilter?.to
    ? `${utils.formatDateIfHave(cycleFilter.from, "date")} – ${utils.formatDateIfHave(cycleFilter.to, "date")}`
    : "";
  const filteredByPlace = !!quickFilter || !_.isEmpty(currentFilter?.idPlants) || !_.isEmpty(currentFilter?.idRegionals);

  // Travel detail modal state
  const [selectedTravel, setSelectedTravel] = useState<Travel>({} as Travel);
  const [openTravelModal, setOpenTravelModal] = useState(false);
  const [loadingTravel, setLoadingTravel] = useState(false);

  /** A new filter from the drawer replaces the regional or plant clicked in the table. */
  const handleDrawerFilter = useCallback((filter: TravelAnalysisFilter) => {
    setQuickFilter(null);

    return handleFilter(filter);
  }, [handleFilter]);

  const handlePlaceClick = useCallback((kind: "regional" | "plant", row: TravelCyclePlaceRow) => {
    if (row.id) setQuickFilter({ kind, id: row.id, name: row.name ?? "" });
  }, []);

  const scrollTo = (ref: React.MutableRefObject<HTMLDivElement | null>) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /** Click on a telemetry chart bar → open the alerts drilldown filtered by that alert type. */
  const handleBarClick = useCallback((alertType: AlertByType) => {
    drilldown.openDrilldown(alertType.id, alertType.description);
  }, [drilldown.openDrilldown]);

  /** Open the standard travel detail modal from a drilldown row. */
  const handleOpenTravel = useCallback(async (travelId: string) => {
    setLoadingTravel(true);
    try {
      const [travelRes, statesRes, alertsRes] = await Promise.all([
        api.get(`travels/read/${travelId}`).then((r) => r.data),
        api.get(`vehicles/states/get-filtered-by-travel/${travelId}`).then((r) => r.data),
        api.post("alerts/read/", { idTravel: travelId }).then((r) => r.data)
      ]);

      const travelDetails = {
        ...travelRes.result,
        vehicle: {
          ...travelRes.result.vehicle,
          states: statesRes.result,
          alerts: alertsRes.result
        }
      };

      setSelectedTravel(travelDetails);
      setOpenTravelModal(true);
    } catch {
      addToast({ type: "error", title: t(TravelAnalysisMessages.noData) });
    } finally {
      setLoadingTravel(false);
    }
  }, [addToast, t]);

  /** Memoized travel detail modal. */
  const travelDetailModal = useMemo(() => {
    if (_.isEmpty(selectedTravel)) return null;

    return (
      <ModalVehicleAndTravelDetails
        vehicle={{ ...selectedTravel.vehicle }}
        travel={selectedTravel}
        open={openTravelModal}
        onClose={() => setOpenTravelModal(false)}
        historical
        overAll
        onlyView
      />
    );
  }, [selectedTravel, openTravelModal]);

  return (
    <>
      <Header title={t(TravelAnalysisMessages.pageTitle)} />
      <Loading loading={loading || cycleLoading} />
      <Container platform={screen.platform}>
        <TopBar>
          <PageTitleSection>
            <div className="page-subtitle">{t(TravelAnalysisMessages.dashboardSubtitle)}</div>
            <div className="page-title">{t(TravelAnalysisMessages.pageTitle)}</div>
            {quickFilter && (
              <QuickFilterChip>
                {`${t(quickFilter.kind === "regional" ? TravelAnalysisMessages.columnRegional : TravelAnalysisMessages.columnPlant)}: `
                  + quickFilter.name}
                <button type="button" aria-label={t(TravelAnalysisMessages.close)} onClick={() => setQuickFilter(null)}>
                  <CancelIcon fontSize="small" />
                </button>
              </QuickFilterChip>
            )}
          </PageTitleSection>
          <TopBarActions>
            <HelpButton onClick={() => setOpenGlossary(true)}>
              <HelpOutlineIcon />
              {t(TravelAnalysisMessages.glossaryButtonLabel)}
            </HelpButton>
            <FilterButton onClick={() => setOpenFilter(true)}>
              <FilterListIcon />
              {t(TravelAnalysisMessages.filterButton)}
            </FilterButton>
          </TopBarActions>
        </TopBar>
        {!loading && (
          <>
            <KpiCards
              summary={summary}
              platform={screen.platform}
              onFailuresClick={() => scrollTo(impactRef)}
              onHardwareClick={() => scrollTo(hardwareRef)}
            />
            <SectionBlock>
              <CycleDaily summary={summary} />
            </SectionBlock>
            <SectionBlock ref={impactRef}>
              <CycleImpact summary={summary} onOutcomeClick={cycleDrilldown.openOutcome} />
            </SectionBlock>
            <SectionBlock>
              <CyclePlaces summary={summary} filteredByPlace={filteredByPlace} onPlaceClick={handlePlaceClick} />
            </SectionBlock>
            <SectionBlock ref={hardwareRef}>
              <HardwareToCheck rows={summary?.hardware ?? []} onOpenVehicle={cycleDrilldown.openVehicle} />
            </SectionBlock>
            <SectionTitle>
              <h3>{t(TravelAnalysisMessages.telemetryTitle)}</h3>
              <p>{t(TravelAnalysisMessages.telemetryHint)}</p>
            </SectionTitle>
            <SectionBlock>
              <AlertDistributionChart
                alertsByType={metrics?.alertsByType ?? []}
                loading={loading}
                onBarClick={handleBarClick}
              />
            </SectionBlock>
            {error && (
              <div style={{ color: "#ff6c5c", textAlign: "center", padding: 16 }}>
                {error}
              </div>
            )}
          </>
        )}
      </Container>
      <QueryFilterTravelAnalysis
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        onFilter={handleDrawerFilter}
      />
      <AlertGlossary open={openGlossary} onClose={() => setOpenGlossary(false)} />
      <CycleDrilldownModal
        open={cycleDrilldown.open}
        view={cycleDrilldown.view}
        data={cycleDrilldown.data}
        loading={cycleDrilldown.loading}
        filterTotal={summary?.total ?? 0}
        periodLabel={periodLabel}
        onClose={cycleDrilldown.close}
        onOpenOutcome={cycleDrilldown.openOutcome}
        onOpenVehicle={cycleDrilldown.openVehicle}
        onOpenTravel={handleOpenTravel}
      />
      <AlertsDrilldownModal
        open={drilldown.open}
        onClose={drilldown.closeDrilldown}
        alerts={drilldown.alerts}
        loading={drilldown.alertsLoading}
        totalCount={drilldown.totalCount}
        selectedAlertType={drilldown.selectedAlertType}
        onLoadMore={drilldown.loadMore}
        onOpenTravel={handleOpenTravel}
      />
      <Loading loading={loadingTravel} />
      {openTravelModal && travelDetailModal}
    </>
  );
};

export default TravelAnalysis;
